import { useEffect, useState } from 'react'
import { hashParam } from '../../app/router/routes'

/**
 * A screen's own filter box, seeded from `?q=` in the address.
 *
 * 🔴 IT EXISTS SO THE CHROME'S SEARCH CAN LAND ON A RECORD RATHER THAN A SCREEN.
 * Picking "Zoë Fernandes" out of the header used to be impossible; now it
 * navigates to `#/school/students?q=Zo%C3%AB`, and without this the Children
 * screen would open showing all thirty-three of them with the one you asked for
 * somewhere in the list. A search result that makes you search again is not a
 * result.
 *
 * ⚠️ IT LISTENS FOR `hashchange` RATHER THAN READING THE HASH ONCE, and that is
 * the whole reason this is a hook instead of a `useState` initialiser. Picking a
 * SECOND child while already on the Children screen changes the address without
 * remounting anything — a lazy initialiser runs once and would leave the first
 * child's name in the box while the address claimed the second. The bug would
 * only appear on the second search in a row, which is exactly the kind that
 * survives a demo.
 *
 * ⚠️ AN ADDRESS WITH NO `?q=` CLEARS IT, RATHER THAN LEAVING THE LAST ONE. Going
 * to Children from the SIDEBAR after arriving there from a search must show the
 * whole roster: a filter still applied from a search the reader has moved on
 * from looks like a screen that has lost most of its rows.
 *
 * ⚠️ TYPING IS NEVER INTERRUPTED. The only thing that writes here is a hash
 * change, and a hash does not change while somebody is typing into a filter.
 */
export function useSeededQuery(): [string, (next: string) => void] {
  const [query, setQuery] = useState(
    () => hashParam(window.location.hash, 'q') ?? '',
  )

  useEffect(() => {
    const onHash = (): void => {
      setQuery(hashParam(window.location.hash, 'q') ?? '')
    }
    window.addEventListener('hashchange', onHash)
    return () => { window.removeEventListener('hashchange', onHash) }
  }, [])

  return [query, setQuery]
}
